/* Import-free on purpose: publicEarlyRequester.ts calls these before any
   module bundling side-effects run, so this file must not import anything.
   Reads runtime-injected window globals and debug-override localStorage keys
   directly. The localStorage keys are duplicated from
   pricing-pages-ui-components/utils/debugOverrides.ts — keep them in sync. */

/* §18 adapter: a single local cast covers the runtime-injected globals the
   HTML wrappers set before our bundle runs. */

const localeWindow = window;
const legacyNav = window.navigator;
const DEBUG_BROWSER_LOCALE_KEY = 'PUBLIC_DEBUG_BROWSER_LOCALE';
const DEBUG_I18N_LANG_KEY = 'PUBLIC_DEBUG_I18N_LANG';
const DEBUG_COUNTRY_CODE_KEY = 'PUBLIC_DEBUG_COUNTRY_CODE';
function readLocalStorage(key) {
  try {
    return localStorage.getItem(key) || '';
  } catch (_unused) {
    return '';
  }
}
export function getNavigatorLanguage() {
  const nav = window.navigator;
  if (nav.languages && nav.languages.length) {
    return nav.languages[0];
  }
  return nav.language || legacyNav.userLanguage || legacyNav.browserLanguage || legacyNav.systemLanguage || '';
}

/* i18n debug override read straight from localStorage so the value is stable
   whether or not applyI18nDebugOverride() has run yet — publicEarlyRequester
   runs before it, DFC callers run after. */
function getEffectiveManualLang() {
  return readLocalStorage(DEBUG_I18N_LANG_KEY) || localeWindow.I18N_MANUAL_LANG || '';
}

/* URL param override for ?overrideLocale=en-US. When present, this takes the
   highest priority so that sales reps can force a non-pilot locale (disabling
   HSoC and using the correct country for product lookups). Accepts basic
   BCP-47 locale strings (e.g. "en-US", "en", "pt-BR"). */
function getOverrideLocaleParam(search = window.location.search) {
  try {
    const value = new URLSearchParams(search).get('overrideLocale') || '';
    return /^[a-zA-Z]{2,3}(-[a-zA-Z]{2,3})?$/.test(value) ? value : '';
  } catch (_unused2) {
    return '';
  }
}

/* Geo country for RPC params. URL overrideLocale param wins first; then the
   debug override; the .in domain is always IN; otherwise the
   COUNTRY_CODE_FROM_LOCALE global the HTML wrapper injects.
   Mirrors getCountryCodeFromLocale() in the in-app countryCodeUtils. */
export function getCountryCode(host = window.location.host, search = window.location.search) {
  var _localeWindow$COUNTRY;
  const overrideLocale = getOverrideLocaleParam(search);
  if (overrideLocale) {
    const countryPart = overrideLocale.split('-')[1];
    if (countryPart) {
      return countryPart.toUpperCase();
    }
  }
  const override = readLocalStorage(DEBUG_COUNTRY_CODE_KEY);
  if (override) {
    return override.toUpperCase();
  }
  if (host.indexOf('hubspot.in') > -1) {
    return 'IN';
  }
  return (_localeWindow$COUNTRY = localeWindow.COUNTRY_CODE_FROM_LOCALE) === null || _localeWindow$COUNTRY === void 0 ? void 0 : _localeWindow$COUNTRY.toUpperCase();
}

/* i18n display language (e.g. 'en-US', 'de') for the Language enum and copy.
   Reads the manual lang (debug override, then HTML wrapper), then navigator.
   For the `browserLocale` RPC param use {@link getEffectiveBrowserLocale}. */
export function getPreferredLanguage() {
  return getEffectiveManualLang() || getNavigatorLanguage();
}

/* The browser locale for RPC `browserLocale` params, always lowercased.
   URL overrideLocale param wins first, then the debug override. Builds
   {lang}-{country} from the manual lang (HTML wrapper / debug override) plus
   the geo country, so the early-request cache key matches what DFC callers
   compute regardless of when each runs. Falls back to the navigator locale.

   This is the "effective" locale because it composes display lang with geo
   country — it is not the same as either the raw navigator locale or the i18n
   display language alone. */
export function getEffectiveBrowserLocale(host = window.location.host, search = window.location.search) {
  const overrideLocale = getOverrideLocaleParam(search);
  if (overrideLocale) {
    const parts = overrideLocale.split('-');
    if (parts[1]) {
      return overrideLocale.toLowerCase();
    }
    const countryCode = getCountryCode(host, search) || getNavigatorLanguage().split('-')[1] || 'US';
    return `${parts[0]}-${countryCode}`.toLowerCase();
  }
  const debugLocale = readLocalStorage(DEBUG_BROWSER_LOCALE_KEY);
  if (debugLocale) {
    return debugLocale.toLowerCase();
  }

  /* manualLang may be a bare language code (en, fr, de) or a full locale
     (pt-br); navigatorLang is typically a full BCP-47 locale (en-US, fr-FR). */
  const manualLang = getEffectiveManualLang();
  const navigatorLang = getNavigatorLanguage();
  if (manualLang) {
    const langCode = manualLang.split('-')[0];
    const countryCode = getCountryCode(host, search) || navigatorLang.split('-')[1] || 'US';
    return `${langCode}-${countryCode}`.toLowerCase();
  }
  return (navigatorLang || 'en-us').toLowerCase();
}